<template>
  <div :dir="pageDirection">
    <!-- HERO -->
    <section class="relative overflow-hidden bg-[#0b0d10] text-white">
      <!-- Background image (LCP) + gradient overlay -->
      <div class="absolute inset-0">
        <NuxtImg
          src="/images/homepage-banner.webp"
          format="webp"
          class="h-full w-full object-cover opacity-60"
          :alt="$t('homePage.hero.imageAlt')"
          width="1200"
          height="675"
          sizes="100vw"
          densities="1"
          fetchpriority="high"
          loading="eager"
          preload
        />
        <!-- Modern overlay -->
        <div
          class="absolute inset-0 bg-gradient-to-b from-black/50 via-black/55 to-black/90"
        />

        <!-- Subtle brand glow accents -->
        <div
          class="pointer-events-none absolute -left-24 top-20 h-72 w-72 rounded-full bg-orange-500/18 blur-3xl"
        />
        <div
          class="pointer-events-none absolute right-0 top-44 h-96 w-96 rounded-full bg-orange-600/10 blur-3xl"
        />
      </div>

      <!-- Content -->
      <div
        class="relative mx-auto max-w-7xl px-6 pb-12 pt-10 sm:pb-16 sm:pt-14 lg:px-8 lg:pt-16"
      >
        <div class="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
          <!-- LEFT -->
          <div class="text-center lg:text-start">
            <!-- small badge -->
            <div
              class="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-white/80"
            >
              <span class="h-2 w-2 rounded-full bg-orange-500" />
              {{ $t('homePage.hero.badge') }}
            </div>

            <h1
              class="mt-5 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl"
            >
              <span class="block">{{ $t('homePage.hero.h1Line1') }}</span>
              <span class="block text-orange-400">{{
                $t('homePage.hero.h1Line2')
              }}</span>
              <span class="block">{{ $t('homePage.hero.h1Line3') }}</span>
            </h1>

            <p
              class="mt-5 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg lg:max-w-2xl"
            >
              {{ $t('homePage.hero.sub') }}
            </p>

            <!-- CTA row -->
            <div
              class="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center lg:justify-start"
            >
              <NuxtLink
                :to="{ path: localePath('/'), hash: '#programs' }"
                data-cy="compare-programs"
                class="group inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 py-3 text-sm font-bold text-black shadow-lg shadow-orange-500/20 ring-1 ring-orange-400/30 transition hover:bg-orange-400 hover:shadow-orange-500/30 focus:outline-none focus:ring-2 focus:ring-orange-400/60"
                @click="track('program_comparison')"
              >
                <svg
                  class="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M13 2 3 14h7l-1 8 12-14h-7l-1-6z" />
                </svg>
                {{ $t('conversion.chooseProgram') }}
              </NuxtLink>

              <!--              <NuxtLink
                :to="$localePath({ name: 'programs' })"
                class="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
              >
                {{ $t('homePage.hero.secondaryCta') }}
              </NuxtLink>-->
            </div>

            <NuxtLink
              :to="localePath({ name: 'stories' })"
              prefetch-on="interaction"
              class="mt-6 inline-flex min-h-11 items-center text-sm font-semibold text-white underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-orange-400"
            >
              {{ t('conversion.trustTitle') }}
              <span class="mx-2" aria-hidden="true">↗</span>
            </NuxtLink>
            <p class="mt-2 max-w-xl text-sm leading-relaxed text-white/80">
              {{ t('conversion.trustText') }}
            </p>
          </div>

          <!-- RIGHT: Device preview (real screenshots) -->
          <div class="relative mx-auto w-full max-w-xl lg:max-w-none">
            <div class="relative">
              <div
                class="pointer-events-none absolute -inset-6 rounded-3xl bg-white/5 blur-2xl"
              />

              <div
                class="rounded-3xl border border-white/10 bg-black/40 p-4 shadow-2xl shadow-black/40 backdrop-blur"
              >
                <HeroDevices
                  tablet-src="/images/screens/tablet.webp"
                  mobile-src="/images/screens/mobile.webp"
                />
                <!-- füge devices webp direkt hier ein -->
                <!--                <NuxtImg
                  src="/images/screens/devices.webp"
                  format="webp"
                  class="w-full h-auto rounded-2xl"
                  alt="Vorschau der Idmanify App auf Tablet und Smartphone"
                  width="800"
                  height="600"
                  decoding="async"
                />-->

                <div
                  class="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/75"
                >
                  <span class="font-semibold text-white">{{
                    $t('homePage.hero.previewCaption')
                  }}</span>
                  <span
                    class="rounded-full bg-orange-500/15 px-2 py-1 text-xs font-semibold text-orange-300 ring-1 ring-orange-400/25"
                  >
                    {{ $t('homePage.hero.previewTag') }}
                  </span>
                </div>
                <AppStoreDownloads />
              </div>

              <!-- Optional: subtle “before/after” cards (small, premium) -->
              <!--              <div
                class="pointer-events-none absolute -top-4 right-0 hidden gap-3 sm:flex"
              >
                <div
                  class="w-36 overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur"
                >
                  <div class="px-3 py-2 text-xs font-semibold text-white/70">
                    Vorher
                  </div>
                  <div
                    class="h-20 bg-gradient-to-b from-white/10 to-transparent"
                  ></div>
                </div>
                <div
                  class="mt-6 w-36 overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur"
                >
                  <div class="px-3 py-2 text-xs font-semibold text-white/70">
                    Nachher
                  </div>
                  <div
                    class="h-20 bg-gradient-to-b from-white/10 to-transparent"
                  ></div>
                </div>
              </div>-->
            </div>
          </div>
        </div>

        <!-- Below-hero USPs -->
        <div class="mt-12 grid gap-4 sm:grid-cols-3">
          <div
            class="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur"
          >
            <div class="mb-2 flex items-center gap-2">
              <svg
                class="h-5 w-5 text-orange-400"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M12 2l1.6 4.6L18 8l-4.4 1.4L12 14l-1.6-4.6L6 8l4.4-1.4L12 2z"
                />
              </svg>
              <p class="font-bold">{{ $t('homePage.hero.usp1Title') }}</p>
            </div>
            <p class="text-sm text-white/70">
              {{ $t('homePage.hero.usp1Text') }}
            </p>
          </div>

          <div
            class="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur"
          >
            <div class="mb-2 flex items-center gap-2">
              <svg
                class="h-5 w-5 text-orange-400"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M13 2 3 14h7l-1 8 12-14h-7l-1-6z" />
              </svg>
              <p class="font-bold">{{ $t('homePage.hero.usp2Title') }}</p>
            </div>
            <p class="text-sm text-white/70">
              {{ $t('homePage.hero.usp2Text') }}
            </p>
          </div>

          <div
            class="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur"
          >
            <div class="mb-2 flex items-center gap-2">
              <svg
                class="h-5 w-5 text-orange-400"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M12 1 21 5v6c0 5-3.8 9.7-9 12C6.8 20.7 3 16 3 11V5l9-4zm-1 13 6-6-1.4-1.4L11 11.2 8.4 8.6 7 10l4 4z"
                />
              </svg>
              <p class="font-bold">{{ $t('conversion.accountTitle') }}</p>
            </div>
            <p class="text-sm text-white/70">
              {{ $t('conversion.accountText') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <ProgramSelection :programs="packages" />
    <section aria-labelledby="steps" class="step-section">
      <div class="step-inner">
        <h2 id="steps">{{ t('conversion.stepsTitle') }}</h2>
        <ol class="step-grid" role="list">
          <li v-for="(s, index) in steps" :key="s.key" class="step-card">
            <span class="step-number" aria-hidden="true">{{
              String(index + 1).padStart(2, '0')
            }}</span>
            <img
              :src="s.icon"
              alt=""
              class="step-icon"
              height="48"
              width="48"
              loading="lazy"
              decoding="async"
            />
            <h3 class="step-title">{{ t(s.label) }}</h3>
            <p class="step-text">{{ t(s.descr) }}</p>
          </li>
        </ol>
      </div>
    </section>

    <LazyHomeEditorial hydrate-on-visible />

    <render-when-visible v-if="shopAvailable" min-height="420px">
      <lazy-products-carousel
        :animate-interval="6000"
        :title="$t('homePage.ourProducts')"
      />
    </render-when-visible>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { useSsrLocalizedApiFetch } from '~/composables/useLocalizedFetch';

const { t, locale } = useI18n();
const pageDirection = computed(() =>
  ['ar', 'ur'].includes(locale.value) ? 'rtl' : 'ltr'
);
const config = useRuntimeConfig();

const localePath = useLocalePath();

/*const i18nHead = useLocaleHead();*/

/*
const i18nHead = useLocaleHead();
useHead({
  title: t('topbar.home'),
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs.lang
  },
  link: [...(i18nHead.value.link || [])],
  meta: [...(i18nHead.value.meta || [])]
});
*/
// SEO: Nuxt 4 empfohlen
/*useSeoMeta({

  ogTitle: t('topbar.home'),
  description: t('homePage.descriptionTop'),
  ogDescription: t('homePage.descriptionTop'),
  ogType: 'website'
  // Wichtig für LCP-Asset (Hero)
  // Hinweis: Nuxt Image setzt Preload per <link> selbst, aber hier doppelt als Fallback:
  // siehe <NuxtPicture preload>
});*/

/*useHead({
  htmlAttrs: { lang: i18nHead.value.htmlAttrs.lang },
  link: [...(i18nHead.value.link || [])],
  meta: [...(i18nHead.value.meta || [])]
});*/

const shopAvailable = computed(() =>
  config.public.SHOP_LANGUAGES.includes(locale.value)
);

/*
const { data: packages } = await useAsyncData(
  'packages',
  async () => {
    const { data } = await useLocalizedApiFetch('/api/packages')
    return data?.value ?? []
  },
  { default: () => [] }
)
*/
// API per SSR laden (keine onMounted nötig)
const { data: packages } = await useSsrLocalizedApiFetch(
  'packages',
  '/api/packages',
  {
    default: () => []
  }
);

const { track } = useConversionTracking();

// Steps Daten (damit v-for + i18n Keys clean sind)
const steps = [
  {
    key: 2,
    icon: '/images/icons/mainsteps/choose-your-package.svg',
    alt: 'homePage.mainSteps.step2alt',
    label: 'homePage.mainSteps.step2label',
    descr: 'homePage.mainSteps.step2descr'
  },
  {
    key: 1,
    icon: '/images/icons/mainsteps/sign-up.svg',
    alt: 'homePage.mainSteps.step1alt',
    label: 'homePage.mainSteps.step1label',
    descr: 'homePage.mainSteps.step1descr'
  },
  {
    key: 3,
    icon: '/images/icons/mainsteps/fill-the-pt-form.svg',
    alt: 'homePage.mainSteps.step3alt',
    label: 'homePage.mainSteps.step3label',
    descr: 'homePage.mainSteps.step3descr'
  },
  {
    key: 4,
    icon: '/images/icons/mainsteps/preparing-program.svg',
    alt: 'homePage.mainSteps.step4alt',
    label: 'homePage.mainSteps.step4label',
    descr: 'homePage.mainSteps.step4descr'
  },
  {
    key: 5,
    icon: '/images/icons/mainsteps/start-to-change.svg',
    alt: 'homePage.mainSteps.step5alt',
    label: 'homePage.mainSteps.step5label',
    descr: 'homePage.mainSteps.step5descr'
  }
];

/**
 * ✅ JSON-LD Rich Snippets
 * (Nur “saubere” Daten eintragen. Ratings nur, wenn belegbar!)
 */
const siteUrl = computed(() =>
  String(config.public.FRONTEND_URL || 'https://www.idmanify.com').replace(
    /\/+$/,
    ''
  )
);

function absoluteUrl(path) {
  return new URL(path || '/', `${siteUrl.value}/`).toString();
}

const homeUrl = computed(() => absoluteUrl(localePath('/')));
const homeTitle = computed(() => t('metaData.index.title'));
const homeDescription = computed(() => t('metaData.index.description'));
const homeOgImage = computed(() => absoluteUrl('/images/homepage-banner.webp'));

useSeoMeta({
  title: () => homeTitle.value,
  description: () => homeDescription.value,
  ogTitle: () => homeTitle.value,
  ogDescription: () => homeDescription.value,
  ogType: 'website',
  ogUrl: () => homeUrl.value,
  ogImage: () => homeOgImage.value,
  twitterCard: 'summary_large_image',
  twitterTitle: () => homeTitle.value,
  twitterDescription: () => homeDescription.value,
  twitterImage: () => homeOgImage.value
});

const jsonLd = computed(() => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${siteUrl.value}/#website`,
      name: 'Idmanify',
      url: `${siteUrl.value}/`,
      inLanguage: locale.value
    },
    {
      '@type': 'WebPage',
      '@id': `${homeUrl.value}#webpage`,
      url: homeUrl.value,
      name: homeTitle.value,
      description: homeDescription.value,
      isPartOf: { '@id': `${siteUrl.value}/#website` },
      inLanguage: locale.value
    }
  ]
}));

useHead(() => ({
  meta: [
    { key: 'og-title', property: 'og:title', content: homeTitle.value },
    {
      key: 'og-description',
      property: 'og:description',
      content: homeDescription.value
    },
    { key: 'og-image', property: 'og:image', content: homeOgImage.value },
    { key: 'twitter-title', name: 'twitter:title', content: homeTitle.value },
    {
      key: 'twitter-description',
      name: 'twitter:description',
      content: homeDescription.value
    },
    { key: 'twitter-image', name: 'twitter:image', content: homeOgImage.value }
  ],
  script: [
    {
      key: 'homepage-json-ld',
      type: 'application/ld+json',
      textContent: JSON.stringify(jsonLd.value)
    }
  ]
}));
// Own route-local params; never inherit an outgoing detail page's slugs.
useSeoI18nParams()({});
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";
.page-top {
  @apply block relative w-full soft-edge;
}

.header-wrapper {
  @apply absolute top-0 sm:left-0 flex flex-col justify-center h-full w-full z-0 sm:w-auto;
}

.header {
  @apply flex flex-col items-center transform sm:-rotate-90 leading-none text-5xl xl:text-6xl z-10;
}

.main-word {
  @apply relative tracking-widest text-white font-bold;
}

/* Alter Brandname-Stil */

/*@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.brand-name {
  @apply text-white text-5xl md:text-6xl font-extrabold tracking-tighter transform transition-transform duration-300 bg-orange-900 bg-linear-to-r from-orange-700 to-orange-900;
}

.brand-name:hover {
  !*
  background-image: linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent);
  *!
  background-image: linear-gradient(
    90deg,
    transparent,
    rgba(255, 0, 0, 0.5),
    transparent
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}*/

/* Markenname Stil */
/*.brand-name {
  @apply text-white text-5xl md:text-6xl font-extrabold tracking-tighter transform transition-transform duration-300 bg-linear-to-r from-orange-700 to-orange-900;
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}*/
.brand-name {
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  @apply text-white text-5xl md:text-6xl font-extrabold tracking-tighter transform transition-transform duration-300 bg-orange-900 bg-linear-to-r from-orange-700 to-orange-900;
}

.brand-name:hover {
  @apply transform scale-105;
  background-image: linear-gradient(
    90deg,
    transparent,
    rgba(255, 0, 0, 0.5),
    transparent
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Beschreibung */
.description {
  @apply text-lg md:text-xl max-w-2xl mx-auto text-gray-300 leading-relaxed;
}

/* Call-to-Action Buttons */
.primary-btn {
  @apply px-8 py-3 bg-orange-600 text-white text-lg font-semibold rounded-md shadow-lg hover:bg-orange-700 transition-all w-full sm:w-auto;
}

.secondary-btn {
  @apply px-8 py-4 bg-orange-500 border border-gray-300 text-white text-lg font-semibold rounded-md hover:bg-gray-300 hover:text-gray-900 transition-all w-full sm:w-auto text-center leading-normal whitespace-normal break-words overflow-hidden flex items-center justify-center;
}
.step-section {
  padding: clamp(3rem, 6vw, 5rem) 1.5rem;
  background: #fff;
  color: #17191d;
}
.step-inner {
  max-width: 76rem;
  margin-inline: auto;
}
.step-inner > h2 {
  max-width: 46rem;
  margin-block-end: 2.5rem;
  font-size: clamp(1.75rem, 3vw, 2.6rem);
  line-height: 1.2;
  font-weight: 800;
  text-wrap: balance;
}
.step-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2rem;
  list-style: none;
  padding: 0;
  margin: 0;
}
.step-grid > li::before {
  content: none !important;
}
.step-card {
  position: relative;
  min-width: 0;
  padding-block-start: 1.5rem;
  border-block-start: 2px solid #d7d9dc;
  text-align: start;
  overflow-wrap: anywhere;
}
.step-number {
  position: absolute;
  inset-inline-end: 0;
  inset-block-start: 1.5rem;
  color: #9a3412;
  font-size: 1rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.step-icon {
  width: 3rem;
  height: 3rem;
  margin-block-end: 1.25rem;
}
.step-title {
  margin-block-end: 0.75rem;
  padding: 0;
  font-size: 1.1rem;
  line-height: 1.4;
  font-weight: 750;
  text-wrap: balance;
}
.step-text {
  color: #444b55;
  font-size: 0.95rem;
  line-height: 1.65;
}
@media (min-width: 640px) {
  .step-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (min-width: 1100px) {
  .step-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}
.bg-picture {
  height: 90vh;
  width: 100%;
}

/*@screen sm {
  .bg-picture {
    max-height: 80vh;
  }
}*/
@layer components {
  .bg-picture {
    @apply sm:max-h-[80vh];
  }
}

.soft-edge:before {
  display: block;
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  -moz-box-shadow: inset 0 20px 20px -7px rgba(0, 0, 0, 1);
  -webkit-box-shadow: inset 0 20px 20px -7px rgba(0, 0, 0, 1);
  box-shadow: inset 0 20px 20px -7px rgba(0, 0, 0, 1);
}
</style>
